import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';

import { ApiError, apiJson } from '@/lib/apiClient';
import { conversationQueryKey, type ConversationMessage } from './useConversation';

/**
 * Send a free-text reply in a conversation (slice 70c).
 *
 * 🔴 NO OPTIMISTIC UI. The reply is NOT shown until the server confirms it reached
 * the send step: on success the thread query is invalidated so the REAL row (read
 * back from the database, with its wamid and status) appears. A WhatsApp message
 * cannot be unsent, so there is no provisional bubble to roll back.
 *
 * The body carries NO `to` field — the recipient is the thread the `conversationId`
 * addresses, resolved server-side. `idempotencyKey` makes a double-submit send once.
 */

export interface SendReplyVars {
  text: string;
  idempotencyKey: string;
}

export interface SendReplyResult {
  message: ConversationMessage;
}

/** A normalised reason a reply did not send, for a translated message on screen. */
export type SendReplyErrorKind =
  | 'window_closed'
  | 'messaging_disabled'
  | 'rate_limited'
  | 'send_failed'
  | 'unknown';

export function sendReplyErrorKind(error: unknown): SendReplyErrorKind {
  if (error instanceof ApiError && error.body && typeof error.body === 'object') {
    const e = (error.body as { error?: unknown }).error;
    if (e === 'window_closed') return 'window_closed';
    if (e === 'messaging_disabled') return 'messaging_disabled';
    if (e === 'rate_limited') return 'rate_limited';
    if (e === 'send_failed') return 'send_failed';
  }
  return 'unknown';
}

export function useSendReply(
  conversationId: string,
): UseMutationResult<SendReplyResult, Error, SendReplyVars> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: SendReplyVars) =>
      apiJson<SendReplyResult>(
        `/api/admin/whatsapp/conversations/${encodeURIComponent(conversationId)}/reply`,
        { method: 'POST', body: JSON.stringify(vars) },
      ),
    // Append the real row by refetching the thread — never an optimistic insert.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: conversationQueryKey(conversationId) });
    },
  });
}
